// library functions

import { TimelineLite } from "gsap/TimelineLite";

import { makeSprite, setParent } from "html-living-framework/lib/Utils/PixiUtils";

// static data
import { GameConfig, LocationID } from "Data/GameConfig";

// gameobjects
import { InteriorItemIconGiveQuest } from "GameObjects/GameInterior/GiveQuest/InteriorItemIconGiveQuest";

// types and interfaces
import type { BGConfig } from "Data/BGConfig";
import type { ItemID } from "Data/GameConfig";
import { AudioCategory } from "html-living-framework/lib/Audio";
import type { IItemConfig } from "Interfaces/IItemConfig";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";

// pubsub topics
import * as HLF from "html-living-framework";

// singleton
import { Application } from "Application";

export class InteriorItemsListGiveQuest extends PIXI.Container {
// children
    public icons: InteriorItemIconGiveQuest[] = [];
    protected bg = this.addChild(new PIXI.Container());

    // gameobject lifecycle
    protected _subscriptions: any[] = [];

    // state
    private animateInTimeline?: TimelineLite;
    private _state!: ILevelState;

    private _animateInMode: "scaleItemsUp" | "slideInFromRight" | "slideInFromTop" = "scaleItemsUp";

    public constructor() {
        super();
        this._subscriptions.push(
            //
        );
    }

    public init(pSize: PIXI.Point, pData: ILevelState) {
        this._state = pData;

        const {items: configItems, ...config} = GameConfig.locations[pData.id];
        const {items: stateItems, ...state} = pData;
        const levelConfig = config.narratives[state.narrative].variations[state.narrativeVariation];

        // lookup and merge state.items with config.items
        const items = (Object.keys(stateItems) as ItemID[]).map((id) => {
            return {...stateItems[id]!, ...configItems[id]!, id};
        });

        const itemslist = {...config.interior.itemslist.give, ...levelConfig.itemslist?.give};
        const bg = itemslist.bg;

        this._animateInMode = itemslist.animateIn;
        this.addBG(bg, pData.id);
        this.spawnItems(items, pData, itemslist);

        if (this._animateInMode === "scaleItemsUp") {
            this.icons.forEach((icon) => icon.visible = false);
        }
        else if (this._animateInMode === "slideInFromRight") {
            this.bg.visible = false;
        }

        return this;
    }

    public animateIn(pData?: {
        onFirstItemShown?: () => void,
    }) {
        this.icons.forEach((icon) => icon.visible = true);
        this.bg.visible = true;

        if (this._animateInMode === "scaleItemsUp") {
            this.animateInTimeline = new TimelineLite();
            this.animateInTimeline
                .staggerFrom(this.icons, 0.5, {pixi: {scale: 0}}, 0.25)
                .call(() => pData?.onFirstItemShown?.(), undefined, this, 0.5)
                .call(() => PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this.icons))
            ;
        }
        else if (this._animateInMode === "slideInFromRight") {
            this.animateInTimeline = new TimelineLite();
            this.animateInTimeline
                .call(() => {
                    Application.instance.audio.play("item-give", 1, false, AudioCategory.SFX);
                })
                .from(this.bg, 2.5, {pixi: {positionX: 1024}})
                .call(() => {
                    pData?.onFirstItemShown?.();
                })
                .call(() => PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this.icons))
                .delay(1)
            ;
        }
        else if (this._animateInMode === "slideInFromTop") {
            this.animateInTimeline = new TimelineLite();
            this.animateInTimeline
                .call(() => {
                    Application.instance.audio.play("item-give", 1, false, AudioCategory.SFX);
                })
                .from(this.bg, 2.5, {pixi: {positionY: -768}})
                .call(() => {
                    pData?.onFirstItemShown?.();
                })
                .call(() => PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this.icons))
                .delay(1)
            ;
        }
    }

    public onResize(pSize: PIXI.Point) {
        //
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        this.animateInTimeline?.kill();
        super.destroy(pOptions);
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
           //
        }
    }

    private spawnItems(
        items: Array<IItemState & IItemConfig>,
        pData: ILevelState,
        opts: {
            floatingItemsY: number,
            highlightFloatingItems: boolean,
            width: number,
            ICON_SPACING: number;
            ICON_SIZE: number;
            columns: number;
            before_icon?: string;
        },
    ) {
        const cols = opts.columns;
        const left = 0 - (opts.width / 2) + (opts.ICON_SIZE / 2) - (opts.ICON_SPACING / 2);
        let x = 0;
        let y = opts.floatingItemsY;
        let row = this.bg.addChild(new PIXI.Container());

        for (let i = 0; i < items.length; i++) {
            if (opts.before_icon !== undefined) {
                const beforeIcon = row.addChild(
                    makeSprite(this._state.id + "/" + opts.before_icon, GameConfig.spritesheets[this._state.id]));
                beforeIcon.anchor.set(1, 0.5);
                beforeIcon.position.set(x, y);
                x += beforeIcon.width * 1.5;
            }
            const newIcon = row.addChild(this.spawnIcon(items[i], pData.id, opts));
            newIcon.position.set(x, y);
            x += opts.ICON_SIZE;
            x += opts.ICON_SPACING;

            // center the row
            // TODO: don't do this every loop
            row.x = left + ((opts.width - row.width) / 2);

            if ( (i + 1) % cols === 0 ) {
                y += opts.ICON_SIZE + opts.ICON_SPACING;
                if (opts.before_icon !== undefined) {
                    y += opts.ICON_SPACING;
                }
                x = 0;
                row = this.bg.addChild(new PIXI.Container());
            }
        }
    }

    private spawnIcon(
        item: IItemState & IItemConfig,
        location: LocationID,
        opts: {
            ICON_SIZE: number;
        },
    ): InteriorItemIconGiveQuest {
        const newIcon = new InteriorItemIconGiveQuest(item, location);
        const xScale = opts.ICON_SIZE / newIcon.width;
        const yScale = opts.ICON_SIZE / newIcon.height;
        newIcon.scale.set(Math.min(xScale, yScale));
        this.icons.push(this.addChild(newIcon));
        return newIcon;
    }

    private addBG(bg: BGConfig, id: LocationID) {
        if (Array.isArray(bg)) {
            for (const layer of bg) {
                const newSprite = makeSprite(id + "/" + layer.filename, GameConfig.spritesheets[id]);
                newSprite.position.x = layer.x;
                newSprite.position.y = layer.y;
                this.bg.addChild(newSprite);
                if (layer.dontAnimateIn) { // HACK
                    setParent(newSprite, this);
                    this.setChildIndex(newSprite, this.getChildIndex(this.bg));
                }
            }
        }
        else {
            this.bg.addChild(makeSprite(id + "/" + bg.filename, GameConfig.spritesheets[id]));
        }
    }
}
